/**
 * SettingsNotifications — Iter 191w · Customer Notifications
 *
 * Vendor-side email template editor for the transactional emails that go
 * out to buyers.  Templates:
 *   • order_confirmation
 *   • shipment_update
 *   • thank_you
 *   • abandoned_cart
 *
 * Each template has: subject + preface + closing.  Empty strings restore
 * the default text.  Tokens supported: {customer_name}, {brand_name}.
 *
 * Persistence:
 *   GET  /api/builder/notifications
 *   PUT  /api/builder/notifications
 *   POST /api/builder/notifications/preview
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Save, Loader2, Mail, ChevronDown, ChevronRight, Eye, RotateCcw, Sparkles,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TOKEN_HINTS = [
  { token: '{customer_name}', desc: "Buyer's first name" },
  { token: '{brand_name}',    desc: 'Your storefront name' },
];

export default function SettingsNotifications() {
  const [templates, setTemplates] = useState({});
  const [expanded, setExpanded]   = useState({});
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [preview, setPreview]     = useState(null); // { key, subject, preface, closing }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/notifications`, { headers: auth() });
        setTemplates(data?.templates || {});
        // Expand the first template by default
        const first = Object.keys(data?.templates || {})[0];
        if (first) setExpanded({ [first]: true });
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const setTpl = (key, patch) =>
    setTemplates((t) => ({ ...t, [key]: { ...(t[key] || {}), ...patch, customized: true } }));

  const save = async () => {
    setSaving(true);
    try {
      // Build the payload — only include fields the vendor customized
      const payload = {};
      for (const [k, tpl] of Object.entries(templates)) {
        payload[k] = {
          subject: tpl.subject,
          preface: tpl.preface,
          closing: tpl.closing,
          enabled: tpl.enabled,
        };
      }
      const { data } = await axios.put(`${API}/builder/notifications`, { templates: payload }, { headers: auth() });
      setTemplates(data.templates || {});
      toast.success('Notification templates saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save templates');
    } finally { setSaving(false); }
  };

  const resetToDefault = async (key) => {
    // Wiping the fields tells the backend to fall back to defaults.
    setTpl(key, { subject: '', preface: '', closing: '' });
    toast.info('Reset — save to apply');
  };

  const openPreview = async (key) => {
    try {
      const { data } = await axios.post(
        `${API}/builder/notifications/preview`,
        { template_key: key, sample: { customer_name: 'Sam' } },
        { headers: auth() },
      );
      setPreview({ key, ...data });
    } catch { toast.error('Could not render preview'); }
  };

  return (
    <div className="relative" data-testid="settings-notifications-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Customer Notifications</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Email templates</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Customize the emails EntrepreNatives sends on your behalf. Leave a field blank to restore its
              default copy. Supported tokens: <code className="text-[#A78BFA]">{`{customer_name}`}</code>,
              <code className="text-[#A78BFA] ml-1">{`{brand_name}`}</code>.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="notifications-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save all'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {Object.entries(templates).map(([key, tpl]) => {
              const isOpen = !!expanded[key];
              return (
                <GlassCard key={key} className="p-0 overflow-hidden" data-testid={`notif-card-${key}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
                      }
                    }}
                    className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.02] transition-colors cursor-pointer outline-none focus:ring-2 focus:ring-[#A78BFA]/40 focus:ring-inset"
                    data-testid={`notif-toggle-${key}`}
                  >
                    <GradientIconTile icon={Mail} ramp={key === 'shipment_update' ? 'cyan' : key === 'thank_you' ? 'emerald' : 'violet'} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-white font-bold font-syne">{tpl.label}</h2>
                        {tpl.customized && <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-[#A78BFA]/20 text-[#A78BFA] font-bold uppercase tracking-wider">Customized</span>}
                        {!tpl.enabled && <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 font-bold uppercase tracking-wider">Off</span>}
                      </div>
                      <p className="text-[11.5px] text-white/55 mt-0.5">{tpl.sent_when}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <Switch
                        checked={!!tpl.enabled}
                        onCheckedChange={(v) => setTpl(key, { enabled: v })}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`notif-toggle-enabled-${key}`}
                      />
                      {isOpen ? <ChevronDown size={16} className="text-white/50" /> : <ChevronRight size={16} className="text-white/50" />}
                    </div>
                  </div>

                  {isOpen && (
                    <div className={`px-5 pb-5 space-y-4 border-t border-white/[0.05] ${tpl.enabled ? '' : 'opacity-45'}`}>
                      <div className="pt-4">
                        <Label className="text-[#94A3B8] text-xs">Subject line</Label>
                        <Input
                          value={tpl.subject || ''}
                          onChange={(e) => setTpl(key, { subject: e.target.value })}
                          placeholder="Subject line"
                          className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                          disabled={!tpl.enabled}
                          data-testid={`notif-subject-${key}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[#94A3B8] text-xs">Opening line</Label>
                        <Textarea
                          rows={3}
                          value={tpl.preface || ''}
                          onChange={(e) => setTpl(key, { preface: e.target.value })}
                          placeholder="Shown right below the header — your first message to the buyer."
                          className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                          disabled={!tpl.enabled}
                          data-testid={`notif-preface-${key}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[#94A3B8] text-xs">Closing note</Label>
                        <Textarea
                          rows={2}
                          value={tpl.closing || ''}
                          onChange={(e) => setTpl(key, { closing: e.target.value })}
                          placeholder="Shown at the bottom, above the sign-off."
                          className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl"
                          disabled={!tpl.enabled}
                          data-testid={`notif-closing-${key}`}
                        />
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex flex-wrap gap-2">
                          {TOKEN_HINTS.map((h) => (
                            <span key={h.token} className="text-[10px] px-2 py-1 rounded bg-white/[0.04] border border-white/10 text-white/70">
                              <code className="text-[#A78BFA]">{h.token}</code> <span className="text-white/40">— {h.desc}</span>
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => resetToDefault(key)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10.5px] font-bold uppercase tracking-wider bg-white/[0.06] hover:bg-white/[0.1] text-white/80 border border-white/10"
                            data-testid={`notif-reset-${key}`}
                          >
                            <RotateCcw size={11} /> Reset
                          </button>
                          <button
                            type="button"
                            onClick={() => openPreview(key)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10.5px] font-bold uppercase tracking-wider bg-[#A78BFA] text-[#0E0E23] hover:bg-white"
                            data-testid={`notif-preview-${key}`}
                          >
                            <Eye size={11} /> Preview
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </GlassCard>
              );
            })}

            <GlassCard className="p-6" data-testid="notif-help-card">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl grid place-items-center bg-[#8B5CF6]/15 border border-[#8B5CF6]/25 flex-shrink-0">
                  <Sparkles size={15} className="text-[#A78BFA]" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Tip</h3>
                  <p className="text-xs text-white/60 mt-1">
                    We handle branding, buyer photos and the receipt table for you — you just customize the words.
                    Turn a template off if you don&rsquo;t want that email sent at all.
                  </p>
                </div>
              </div>
            </GlassCard>
          </>
        )}
      </div>

      {/* Preview modal */}
      {preview && (
        <PreviewModal preview={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}

function PreviewModal({ preview, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[100] grid place-items-center px-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="notif-preview-modal"
    >
      <div className="absolute inset-0 bg-[#08081A]/80 backdrop-blur-md" />
      <div
        className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#171A2C]/95 shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-white/[0.06] flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#A78BFA] font-bold">Preview · buyer view</p>
            <h3 className="text-white font-bold text-lg mt-1">Subject: {preview.subject}</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-xs uppercase tracking-wider font-bold" data-testid="notif-preview-close">Close</button>
        </div>

        {/* Faux email body */}
        <div className="bg-[#0E0E23] p-6">
          <div className="rounded-xl overflow-hidden shadow-xl" style={{ background: '#0E0E23', border: '1px solid rgba(139,92,246,0.25)' }}>
            <div style={{ background: 'linear-gradient(135deg,#8B5CF6,#3B82F6)', padding: '20px', textAlign: 'center' }}>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: 700, letterSpacing: 2, margin: 0 }}>{(preview.brand || '').toUpperCase()}</p>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[#C4B5FD] text-sm leading-relaxed">Hi Sam,</p>
              <p className="text-slate-200 text-sm leading-relaxed">{preview.preface}</p>
              <div className="my-4 p-3 rounded-lg bg-white/[0.04] border border-white/10">
                <p className="text-[11px] text-slate-400 italic">— Sample content (order details / tracking / etc.) —</p>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed">{preview.closing}</p>
              <p className="text-slate-400 text-xs pt-3">— The {preview.brand} team</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
